import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import MainHeader from "../../shared/MainHeader";
import { useFormik } from "formik";
import * as Yup from "yup";
import DateInput from "../../shared/DateInput";
import { GET, PUT } from "../../api/ApiService";
import EncryptionHelper from "../../shared/EncryptionHelper";
import CaseSearchDropdown from "../../shared/CaseSearchDropdown";
import moment from "moment";
import UserSearchDropdown from "../../shared/UserSearchDropdown";
import { useSelector, useDispatch } from "react-redux";
import ExportActivitiesTable from "./ExportActivitiesTable";
import { useNavigate } from "react-router-dom";
import { storeEditDar } from "../../slices/editDarSlice";

const ExportActivities = () => {
  const userDetails = useSelector((state) => state.userReducer.userDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [showExport, setShowExported] = useState(true);
  const [totalTime, setTotalTime] = useState();
  const [resetDropdown, setResetDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeBtn, setActiveBtn] = useState("");
  const [conflictsOnly, setConflictsOnly] = useState(false);

  // ✅ Edit click handler → navigates to /editdar
  const handleEditClick = (row) => {
    dispatch(storeEditDar(row));
    navigate("/editdar");
  };

  useEffect(() => {
    const caseId = formik.values?.case?.CaseID ? formik.values.case.CaseID : "";
    const socialwId = formik.values.socialWorker?.id
      ? formik.values.socialWorker.id
      : null;

    fetchActivities(formik.values.startDate, formik.values.endDate, caseId, socialwId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset dropdown trigger
  useEffect(() => {
    if (resetDropdown) setResetDropdown(false);
  }, [resetDropdown]);

  const getExportActivities = async (startDate, endDate, caseid, socialwId) => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      caseId: caseid ?? "",
      socialwId: socialwId ?? "",
    });

    const resp = await GET(
      `daily-activity/export-activity-report?${params.toString()}`
    );
    const encryptedData = resp?.encryptedData;
    const decryptedData = EncryptionHelper.decryptData(encryptedData);
    return decryptedData?.result ?? [];
  };

  const toConflictRange = (dateStr, kind) => {
    if (!dateStr) return "";
    return kind === "start" ? `${dateStr} 00:00:00` : `${dateStr} 23:59:59`;
  };

  const fetchActivities = async (startDate, endDate, caseid, socialwId) => {
    setLoading(true);
    try {
      const result = await getExportActivities(
        startDate,
        endDate,
        caseid,
        socialwId
      );
      setData(result);
      setShowExported(true);

      const timeStrings = result?.map((item) => item.TimeSpent);
      setTotalTime(sumTime(timeStrings));
    } catch (err) {
      console.warn("Error fetching activities:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConflictActivities = async (
    startDate,
    endDate,
    caseid,
    socialwId
  ) => {
    setLoading(true);
    try {
      const conflictParams = new URLSearchParams({
        startDate: toConflictRange(startDate, "start"),
        endDate: toConflictRange(endDate, "end"),
      });

      const conflictUserId = socialwId ?? userDetails?.id ?? "";
      const asNumber = Number(conflictUserId);
      if (Number.isFinite(asNumber) && asNumber > 0) {
        conflictParams.set("userId", String(asNumber));
      }

      const conflictResp = await GET(
        `daily-activity/conflicts?${conflictParams.toString()}`
      );
      const conflictDecrypted = EncryptionHelper.decryptData(
        conflictResp?.encryptedData
      );
      const conflicts = conflictDecrypted?.conflicts ?? [];

      const reportIdSet = new Set();
      for (const row of conflicts) {
        if (row?.ReportID_A !== undefined && row?.ReportID_A !== null) {
          reportIdSet.add(String(row.ReportID_A));
        }
        if (row?.ReportID_B !== undefined && row?.ReportID_B !== null) {
          reportIdSet.add(String(row.ReportID_B));
        }
      }

      if (reportIdSet.size === 0) {
        setData([]);
        setShowExported(true);
        setTotalTime("00:00");
        return;
      }

      const allActivities = await getExportActivities(
        startDate,
        endDate,
        caseid,
        socialwId
      );
      const onlyConflicts = allActivities.filter((item) =>
        reportIdSet.has(String(item?.ReportID))
      );

      setData(onlyConflicts);
      setShowExported(true);
      setTotalTime(sumTime(onlyConflicts.map((item) => item.TimeSpent)));
    } catch (err) {
      console.warn("Error fetching conflict activities:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportClickHandler = async () => {
    const reportIds = data?.map((item) => item.ReportID);
    const encryptData = EncryptionHelper.encryptData(reportIds);
    try {
      await PUT(`daily-activity/export-dar`, { encryptData });
      const caseId = formik.values?.case?.CaseID ? formik.values.case.CaseID : "";
      const socialwId = formik.values.socialWorker?.id
        ? formik.values.socialWorker.id
        : null;

      if (conflictsOnly) {
        fetchConflictActivities(
          formik.values.startDate,
          formik.values.endDate,
          caseId,
          socialwId
        );
      } else {
        fetchActivities(
          formik.values.startDate,
          formik.values.endDate,
          caseId,
          socialwId
        );
      }
    } catch (err) {
      console.warn("Error exporting DAR:", err);
    }
  };

  const viewExportHandler = async () => {
    setActiveBtn("viewExport");
    setConflictsOnly(false);
    try {
      const response = await GET("daily-activity/all-exported-dar");
      setShowExported(false);
      setData(response);
    } catch (err) {
      console.warn("Error viewing exported DAR:", err);
    }
  };

  const formik = useFormik({
    initialValues: {
      startDate: (() => {
        const currentDate = new Date();
        const startDate = new Date();
        startDate.setDate(currentDate.getDate() - 30);
        return startDate.toISOString().split("T")[0];
      })(),
      case: null,
      endDate: (() => {
        const currentDate = new Date();
        const localDate = new Date(
          currentDate.getTime() - currentDate.getTimezoneOffset() * 60000
        );
        return localDate.toISOString().split("T")[0];
      })(),
      socialWorker: null,
    },
    validationSchema: Yup.object({
      startDate: Yup.date().required("Start date is required"),
      endDate: Yup.date()
        .required("End date is required")
        .min(Yup.ref("startDate"), "End date must be after start date"),
    }),
    onSubmit: (values) => {
      const caseId = values.case?.CaseID ? values.case.CaseID : "";
      const socialwId = values.socialWorker?.id ? values.socialWorker.id : null;

      if (conflictsOnly) {
        fetchConflictActivities(values.startDate, values.endDate, caseId, socialwId);
        setActiveBtn("conflicts");
      } else {
        fetchActivities(values.startDate, values.endDate, caseId, socialwId);
        setActiveBtn("search");
      }
    },
  });

  const handleConflictsToggle = (event) => {
    const checked = event.target.checked;
    setConflictsOnly(checked);

    const caseId = formik.values?.case?.CaseID ? formik.values.case.CaseID : "";
    const socialwId = formik.values.socialWorker?.id
      ? formik.values.socialWorker.id
      : null;

    if (checked) {
      setActiveBtn("conflicts");
      fetchConflictActivities(
        formik.values.startDate,
        formik.values.endDate,
        caseId,
        socialwId
      );
    } else {
      setActiveBtn("search");
      fetchActivities(
        formik.values.startDate,
        formik.values.endDate,
        caseId,
        socialwId
      );
    }
  };

  const downloadCSV = () => {
    if (!data?.length) return;
    
    const formatTime = (time) =>
      time ? moment(time).format("h:mm A") : "";

    const formattedData = data.map((item) => ({
      "User Name": item.UserName,
      "Time Spent": item.TimeSpent,
      "Transaction Date": item.TransactionDate,
      "Start Time": formatTime(item.StartTime),
      "Finish Time": formatTime(item.FinishTime),
      "Service or Activity": item.ServiceOrActivity,
      "Case Name": item.CaseName,
      "Case Number": item.CaseNumber,
      "Created At": item.CreatedAt,
    }));

    const headers = Object.keys(formattedData[0]).join(",") + "\n";
    const rows = formattedData
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = headers + rows;

    const formatDateForFile = (dateStr) =>
      dateStr ? moment(dateStr).format("YYYY-MM-DD") : "unknown";
    const fileName = `DailyActivityReports_${formatDateForFile(
      formik.values.startDate
    )}_to_${formatDateForFile(formik.values.endDate)}.csv`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  function sumTime(times) {
    if (!Array.isArray(times)) return "00:00";
    let totalMinutes = 0;
    for (const time of times) {
      if (!time) continue;
      const [hours, minutes] = time.split(":").map(Number);
      totalMinutes += hours * 60 + minutes;
    }
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    return `${String(totalHours).padStart(2, "0")}:${String(
      remainingMinutes
    ).padStart(2, "0")}`;
  }

  return (
    <Box>
      <MainHeader headingText="Export Report" />
      <Stack
        gap={3}
        p={3}
        sx={{
          border: "1px solid #C1C1C1",
          minHeight: "80vh",
        }}
      >
        <Stack direction="row" gap={3}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "700",
              lineHeight: "21.92px",
              textAlign: "left",
            }}
          >
            My Activities:
          </Typography>
        </Stack>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={5}>
            {/* Start Date */}
            <Grid item xs={12} sm={4}>
              <Stack>
                <Typography sx={{ textAlign: "left" }}>
                  Start Date<span style={{ color: "red" }}>*</span>
                </Typography>
                <DateInput
                  name="startDate"
                  onChangeValue={formik.handleChange}
                  onBlurValue={formik.handleBlur}
                  value={formik.values.startDate}
                />
                {formik.touched.startDate && formik.errors.startDate && (
                  <Typography variant="caption" color="error" textAlign="left">
                    {formik.errors.startDate}
                  </Typography>
                )}
              </Stack>
            </Grid>

            {/* Case */}
            <Grid item xs={12} sm={6}>
              <Stack>
                <Typography sx={{ textAlign: "left" }}>Case:</Typography>
                <CaseSearchDropdown
                  name="case"
                  value={formik.values.case}
                  onChange={(event, newValue) =>
                    formik.setFieldValue("case", newValue)
                  }
                  onBlur={formik.handleBlur}
                />
              </Stack>
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={4}>
              <Stack>
                <Typography sx={{ textAlign: "left" }}>
                  End Date<span style={{ color: "red" }}>*</span>
                </Typography>
                <DateInput
                  name="endDate"
                  onChangeValue={formik.handleChange}
                  onBlurValue={formik.handleBlur}
                  value={formik.values.endDate}
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <Typography variant="caption" color="error" textAlign="left">
                    {formik.errors.endDate}
                  </Typography>
                )}
              </Stack>
            </Grid>

            {/* Collaborator */}
            <Grid item xs={12} sm={6}>
              <Stack>
                <Typography sx={{ textAlign: "left" }}>
                  Collaborator:
                </Typography>
                <UserSearchDropdown
                  name="socialWorker"
                  value={formik.values.socialWorker}
                  onChange={(event, newValue) =>
                    formik.setFieldValue("socialWorker", newValue)
                  }
                  onBlur={formik.handleBlur}
                  reset={resetDropdown}
                />
              </Stack>
            </Grid>

            {/* Conflicts */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={conflictsOnly}
                    onChange={handleConflictsToggle}
                    color="success"
                  />
                }
                label="Show only overlapping activities in selected date range"
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Stack gap={2} direction="row">
                <Button
                  type="submit"
                  variant={
                    activeBtn === "search" || activeBtn === "conflicts"
                      ? "outlined"
                      : "contained"
                  }
                  color="shamrock"
                >
                  Search
                </Button>

                {userDetails?.role === 3 && (
                  <Button
                    variant={
                      activeBtn === "viewExport" ? "outlined" : "contained"
                    }
                    color="shamrock"
                    onClick={viewExportHandler}
                  >
                    View Exported
                  </Button>
                )}

                {data.length > 0 && showExport && (
                  <Button
                    variant="contained"
                    color="shamrock"
                    sx={{ color: "white" }}
                    onClick={exportClickHandler}
                  >
                    Export
                  </Button>
                )}
              </Stack>
            </Grid>

            {/* CSV Download */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="shamrock"
                sx={{ color: "white" }}
                onClick={downloadCSV}
              >
                Download as CSV
              </Button>
            </Grid>

            {/* Table */}
            <Grid item xs={12}>
              {!loading && (
                <ExportActivitiesTable
                  data={data}
                  onEditClick={handleEditClick}
                />
              )}
            </Grid>

            {/* Total Time */}
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="center">
                <Typography>
                  <b>Total Time:</b> <span>{totalTime}</span> hours
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Stack>
    </Box>
  );
};

export default ExportActivities;
